import { Button, Card, Input, Space, Table, Tag, type TableProps } from "antd";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  OrderStatusOption,
  type Order,
  type OrderItem,
  type OrderStatus,
} from "@fitness-app/data-models/entities/Order";
import { PaymentTypeOption } from "@fitness-app/data-models/entities/Product";
import { getHumanPrice } from "@fitness-app/utils/src/products/getHumanPrice";

import { useProductsList } from "~/modules/Dashboard/AppStats/hooks/useProductsList";
import InvoiceDataInfo from "~/modules/Products/components/InvoiceDataInfo/InvoiceDataInfo";
import {
  INVOICE_ERROR_FILTER,
  useProductsTransactions,
  type OrderWithProductName,
} from "~/modules/Products/ProductsTransactions/useProductsTransactions";
import { OrderPaidAtCell } from "~/modules/Products/helpers/OrderPaidAtCell";
import { OrderStatusCell } from "~/modules/Products/helpers/OrderStatusCell";
import { CrossSellInfo } from "../components/CrossSellInfo/CrossSellInfo";
import InvoiceNumberInfo from "../components/InvoiceNumberInfo/InvoiceNumberInfo";
import { getTableChangeAction } from "./getTableChangeAction";
import { OrderPaymentCell } from "../helpers/OrderPaymentCell";

const statusWithProductClient: Order["status"][] = [
  OrderStatusOption.Paid,
  OrderStatusOption.Finalized,
  OrderStatusOption.Imported,
  OrderStatusOption.Refunded,
];

export const ProductsTransactions = (): React.ReactElement => {
  const { t } = useTranslation(["products", "common", "client"]);
  const { list } = useProductsList();
  const {
    data,
    clearSearch,
    setSearchTerm,
    searchTerm,
    currentPage,
    pageSize,
    filteredStatuses,
    setCurrentPage,
    setFilteredStatuses,
    filteredProducts,
    setFilteredProducts,
    filteredInvoice,
    setFilteredInvoice,
    isFetching,
    isLoading,
  } = useProductsTransactions();

  const onSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      if (searchTerm) {
        clearSearch();
      }
      return;
    }
    setSearchTerm(trimmed);
    setCurrentPage(1);
  };

  const handleChange: TableProps<OrderWithProductName>["onChange"] = (pagination, filters) => {
    const action = getTableChangeAction({
      pagination,
      filters,
      currentState: {
        currentPage,
        filteredStatuses,
        filteredProducts,
        filteredInvoice,
      },
    });

    switch (action.type) {
      case "status":
        setCurrentPage(1);
        setFilteredStatuses(action.statuses);
        break;
      case "products":
        setCurrentPage(1);
        setFilteredProducts(action.products);
        break;
      case "invoice":
        setCurrentPage(1);
        setFilteredInvoice(action.invoice);
        break;
      case "pagination":
        setCurrentPage(action.page);
        break;
    }
  };

  return (
    <Card>
      <Space direction="vertical" style={{ width: "100%" }} size={24}>
        <div style={{ width: "100%", display: "flex", alignItems: "center" }}>
          <Input.Search
            placeholder={t<string>("search.placeholder")}
            onSearch={onSearch}
            allowClear
            style={{ width: 300, margin: "20px 0" }}
          />
          {filteredProducts?.length || filteredStatuses?.length || filteredInvoice !== null ? (
            <Button
              onClick={() => {
                setCurrentPage(1);
                setFilteredProducts([]);
                setFilteredStatuses([]);
                setFilteredInvoice(null);
              }}
              type="link"
            >
              {t("search.reset")}
            </Button>
          ) : null}
        </div>

        <Table<OrderWithProductName>
          rowKey="id"
          dataSource={data?.list || []}
          scroll={{ x: true }}
          loading={isLoading || isFetching}
          onChange={handleChange}
          pagination={{
            current: currentPage,
            pageSize,
            total: data?.count || 0,
            pageSizeOptions: ["50"],
            showSizeChanger: false,
          }}
          expandedRowRender={(order) => <InvoiceDataInfo orderClient={order.client} />}
        >
          <Table.Column<OrderWithProductName>
            title={t("productTransactions.tableHeader.index")}
            width={50}
            dataIndex="index"
            key="index"
            render={(name, row, i) => (currentPage > 1 ? (currentPage - 1) * pageSize + i + 1 : i + 1)}
          />
          <Table.Column<OrderWithProductName>
            title={t("productTransactions.tableHeader.name")}
            dataIndex="name"
            key="name"
            render={(_, row) =>
              row.client.lastName || row.client.firstName
                ? `${row.client.lastName || ""} ${row.client.firstName || ""}`.trim()
                : "-"
            }
            // sorter={(a, b) => a.client.lastName?.localeCompare(b.client.lastName)}
          />
          <Table.Column<OrderWithProductName>
            title={t("productTransactions.tableHeader.email")}
            dataIndex="clientEmail"
            key="clientEmail"
            // sorter={(a, b) => a.clientEmail.length - b.clientEmail.length}
            render={(email, row) =>
              statusWithProductClient.includes(row.status) ||
              row.connectedSubscription ||
              row.paymentType === PaymentTypeOption.Recurring ? (
                <Link to={`/products/${row.product}/clients/${email}`}>{email}</Link>
              ) : (
                email
              )
            }
          />
          <Table.Column<OrderWithProductName>
            title={t("productTransactions.tableHeader.createdAt")}
            dataIndex="createdAt"
            key="createdAt"
            render={(createdAt: string) => dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
            defaultSortOrder="descend"
            // sorter={(a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix()}
          />
          <Table.Column<OrderWithProductName>
            title={t("productTransactions.tableHeader.productName")}
            dataIndex="productInfo"
            key="productInfo"
            filteredValue={filteredProducts}
            render={(_, row) => <Link to={`/products/${row.product}/`}>{row.productInfo.name}</Link>}
            filters={list.map((item) => ({
              value: item.value,
              text: item.label,
            }))}
          />
          <Table.Column<OrderWithProductName>
            title={t("productTransactions.tableHeader.price")}
            dataIndex="price"
            key="price"
            render={(price: OrderItem, row) =>
              price ? (
                <Space>
                  <b className="whitespace-nowrap">{getHumanPrice(price.amount, price.currency)}</b>
                  <CrossSellInfo crossSell={row.crossSell} />
                </Space>
              ) : (
                "-"
              )
            }
            // sorter={(a, b) =>
            //   (!a.price || a.price.free ? 0 : a.price.amount) - (!b.price || b.price.free ? 0 : b.price.amount)
            // }
          />
          <Table.Column<OrderWithProductName>
            title={t("productTransactions.tableHeader.status")}
            dataIndex="status"
            key="status"
            filteredValue={filteredStatuses as OrderStatus[]}
            filters={Object.values(OrderStatusOption).map((status) => ({
              value: status,
              text: t(`productTransactions.type.${status.toLowerCase()}`),
            }))}
            render={(_type, record) => <OrderStatusCell order={record} />}
          />
          <Table.Column<OrderWithProductName>
            title={t("productTransactions.tableHeader.paidAt")}
            dataIndex="paidAt"
            key="paidAt"
            render={(_value, record) => <OrderPaidAtCell order={record} />}
          />
          <Table.Column<OrderWithProductName>
            title={t("productClients.tableHeader.paymentType")}
            dataIndex="paymentType"
            key="paymentType"
            render={(type, record) => <Tag>{t(`client:type.${record.installments ? "installments" : type}`)}</Tag>}
          />

          <Table.Column<OrderWithProductName>
            title={t("productTransactions.tableHeader.paymentGateway")}
            dataIndex="paymentGateway"
            key="paymentGateway"
            render={(_paymentGateway: string, record) => <OrderPaymentCell order={record} />}
          />
          <Table.Column<OrderWithProductName>
            title={t("productClients.tableHeader.options")}
            dataIndex="action"
            key="action"
            render={(_, row) => {
              if (row.customerId) {
                return <Link to={`/products/customers/${row.customerId}`}>{t(`common:details`)}</Link>;
              }
              return statusWithProductClient.includes(row.status) ||
                row.connectedSubscription ||
                row.paymentType === PaymentTypeOption.Recurring ? (
                <Link to={`/products/${row.product}/clients/${row.clientEmail}`}>{t(`common:details`)}</Link>
              ) : (
                "-"
              );
            }}
          />
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.invoice")}
            dataIndex="connectedInvoice"
            key="connectedInvoice"
            filteredValue={filteredInvoice !== null ? [filteredInvoice] : []}
            filters={[
              {
                text: t("common:yes"),
                value: true,
              },
              { text: t("common:no"), value: false },
              { text: t("common:errorInvoices"), value: INVOICE_ERROR_FILTER },
            ]}
            render={(connectedInvoice: Order["connectedInvoice"], order: Order) => (
              <div className="flex w-full items-center justify-between">
                <InvoiceNumberInfo invoice={connectedInvoice} withId orderId={order.id} productId={order.product} />
              </div>
            )}
          />
        </Table>
      </Space>
    </Card>
  );
};
